import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import type { Page } from './types';

interface CursorList<T> {
  items: T[];
  error: string | null;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
}

const sep = (p: string) => (p.includes('?') ? '&' : '?');

/**
 * Cursor-paginated GET list. `basePath` should already carry any filter
 * query params (status/q/type/…) but not cursor/limit — those are added
 * here. Resets to page one whenever `basePath` or `actingFor` changes. Pass
 * null to skip fetching entirely (e.g. a tab that isn't active yet).
 */
export function useCursorList<T>(basePath: string | null, opts?: { limit?: number; actingFor?: string }): CursorList<T> {
  const limit = opts?.limit ?? 50;
  const actingFor = opts?.actingFor;
  const [items, setItems] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(basePath));
  const [loadingMore, setLoadingMore] = useState(false);
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    setItems([]);
    setNextCursor(null);
    if (!basePath) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get<Page<T>>(`${basePath}${sep(basePath)}limit=${limit}`, actingFor ? { actingFor } : undefined)
      .then((page) => {
        if (!alive.current) return;
        setItems(page.items);
        setNextCursor(page.nextCursor);
      })
      .catch((e: unknown) => {
        if (alive.current) setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
    return () => {
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath, actingFor, limit, tick]);

  const loadMore = useCallback(() => {
    if (!basePath || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    api
      .get<Page<T>>(`${basePath}${sep(basePath)}limit=${limit}&cursor=${encodeURIComponent(nextCursor)}`, actingFor ? { actingFor } : undefined)
      .then((page) => {
        setItems((prev) => [...prev, ...page.items]);
        setNextCursor(page.nextCursor);
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed'))
      .finally(() => setLoadingMore(false));
  }, [basePath, nextCursor, loadingMore, limit, actingFor]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { items, error, loading, loadingMore, hasMore: Boolean(nextCursor), loadMore, reload };
}
